import { useMemo } from 'react'
import { useCacheTable } from './hooks.js'
import type { Vorschlag } from './Vorschlagsfeld.js'

/**
 * Leistungen und Material aus dem Katalog – als Vorschläge zu einer Bezeichnung.
 *
 * Gemeldet vom Kunden: In der Express-Erfassung findet die Suche Leistungen
 * und Material, in einer bestehenden Rechnung oder Offerte nicht. Dort musste
 * jede Position von Hand abgetippt werden – mit Einheit und Preis aus dem Kopf.
 *
 * Die Suche ist dieselbe wie in `ExpressInvoice`: der Katalog aus dem lokalen
 * Zwischenspeicher (`useCacheTable`, beim Abgleich ohnehin da), aktive
 * Einträge, Treffer in Code oder Bezeichnung, höchstens acht. Kein Aufruf ans
 * Netz pro Tastendruck und keine zweite Suchlogik.
 *
 * Angelegt wird hier nichts. Ein Eintrag entsteht im Katalog nur über den
 * eigenen Knopf «In Katalog» mit Rückfrage.
 */

export interface Katalogeintrag {
  id: string
  code: string
  name: string
  unit: string
  unitPriceRp: number
  kind: 'arbeit' | 'material'
  active?: boolean
}

const chf = (rp: number): string =>
  (rp / 100).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function useKatalogvorschlaege(text: string): {
  vorschlaege: Vorschlag[]
  eintrag: (schluessel: string) => Katalogeintrag | undefined
} {
  const { rows } = useCacheTable('catalogItems')
  const katalog = useMemo(
    () => (rows as unknown as Katalogeintrag[]).filter((c) => c && c.active !== false),
    [rows]
  )
  const treffer = useMemo(() => {
    const q = text.trim().toLowerCase()
    if (!q) return []
    return katalog.filter((c) => `${c.code ?? ''} ${c.name}`.toLowerCase().includes(q)).slice(0, 8)
  }, [text, katalog])

  return {
    vorschlaege: treffer.map((c) => ({
      wert: c.name,
      haupt: c.name,
      neben: `${c.code ? `${c.code} · ` : ''}${c.kind === 'material' ? 'Material' : 'Leistung'} · CHF ${chf(
        c.unitPriceRp
      )} / ${c.unit}`,
      schluessel: c.id
    })),
    eintrag: (schluessel) => katalog.find((c) => c.id === schluessel)
  }
}
